/**
 * AssignPlanModal — assign a catalogued plan to a lab (MANAGEMENT 14 pricing). POSTs
 * /management/pricing/labs/:id/assign, which resolves the tier's module bundle and delegates to the
 * subscriptions projection writer (drives the on-box entitlement gate). The operator supplies the target
 * lab id (numeric) + the subscription statut + an optional motif. The plan is fixed (the card the operator
 * clicked). Full-CRUD-by-reissue upstream: assigning again reissues the projection.
 *
 * AA: labelled fields, role="alert" error, loading submit. FR i18n; the lab id rides `.nv-ltr-island`.
 */
import { useState } from "react";
import { Button, FormField, Input, Modal, Select, Textarea, useToast } from "@/ui";
import { ApiError } from "@/api";
import { useT } from "@/app";
import { useAssignPlan } from "../screens/pricing.api";
import type { PlanRow } from "../screens/pricing.types";

/** The central subscription statuts an operator may assign (mirrors the server SUBSCRIPTION_STATUTS). */
const ASSIGN_STATUTS = ["ESSAI", "ACTIVE", "PAST_DUE", "SUSPENDUE", "EXPIREE", "ANNULEE"] as const;

export interface AssignPlanModalProps {
  plan: PlanRow | undefined;
  onClose: () => void;
}

export function AssignPlanModal({ plan, onClose }: AssignPlanModalProps) {
  const { t } = useT();
  const toast = useToast();
  const open = plan !== undefined;

  const [labId, setLabId] = useState("");
  const [statut, setStatut] = useState<(typeof ASSIGN_STATUTS)[number]>("ACTIVE");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // The mutation is keyed on the entered lab id; "0" is an inert stand-in while empty (submit gates first).
  const assign = useAssignPlan(labId.trim() || "0");

  const seedKey = open ? plan!.code : "_closed";
  const [seeded, setSeeded] = useState<string | null>(null);
  if (open && seeded !== seedKey) {
    setSeeded(seedKey);
    setLabId("");
    setStatut("ACTIVE");
    setReason("");
    setError(null);
  }
  if (!open && seeded !== null) setSeeded(null);

  const submit = () => {
    setError(null);
    if (!/^\d+$/.test(labId.trim())) {
      setError(t("mgmt.pricing.assign.labIdRequired"));
      return;
    }
    assign.mutate(
      {
        plan_code: plan!.code,
        statut,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t("mgmt.pricing.assign.done"));
          onClose();
        },
        onError: (err) => setError(err instanceof ApiError ? err.message : t("mgmt.pricing.error")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={open ? t("mgmt.pricing.assign.title", { plan: plan!.nom }) : ""}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("mgmt.pricing.cancel")}
          </Button>
          <Button onClick={submit} loading={assign.isPending}>
            {t("mgmt.pricing.assign.submit")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <FormField label={t("mgmt.pricing.assign.labId")} hint={t("mgmt.pricing.assign.labId.hint")} required>
          {(p) => (
            <Input
              {...p}
              value={labId}
              onChange={(e) => setLabId(e.target.value)}
              inputMode="numeric"
              className="nv-ltr-island"
            />
          )}
        </FormField>
        <FormField label={t("mgmt.pricing.assign.statut")} required>
          {(p) => (
            <Select
              {...p}
              value={statut}
              onChange={(e) => setStatut(e.target.value as (typeof ASSIGN_STATUTS)[number])}
              options={ASSIGN_STATUTS.map((s) => ({ value: s, label: t(`mgmt.pricing.subStatut.${s}`) }))}
            />
          )}
        </FormField>
        <FormField label={t("mgmt.pricing.assign.reason")} hint={t("mgmt.pricing.assign.reason.hint")}>
          {(p) => <Textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />}
        </FormField>
        {error && (
          <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
